import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import StepNumber from "../components/StepNumber";
import PageIntro from "../components/PageIntro";
import heroPackages from "../assets/heroes/hero-packages.webp";
import CtaBand from "../components/CtaBand";
import ctaRentals from "../assets/heroes/hero-rentals.webp";
import { PAGES } from "../seoContent";
import { whatsappLink, WHATSAPP_MESSAGES } from "../utils/whatsapp";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const displayPrice = (n) =>
  Number.isInteger(Number(n))
    ? `£${Number(n).toLocaleString("en-GB")}`
    : money(n);

const getTodayUK = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const FORM_DEFAULTS = {
  fullName: "",
  email: "",
  phone: "",
  eventType: "Wedding",
  eventDate: "",
  location: "",
  startTime: "",
  coverageNotes: "",
};

export default function EventPackages() {
  const { apiFetch } = useApi();
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [packageType, setPackageType] = useState(() => {
    return localStorage.getItem("kinews_event_tab") || "wedding";
  });

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "wedding-package-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["fullName", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  useEffect(() => {
    localStorage.setItem("kinews_event_tab", packageType);
    
    // Ensure form state matches the loaded tab
    if (packageType === "general") {
      updateField("eventType", "");
    } else if (packageType === "wedding") {
      updateField("eventType", "Wedding");
    }
  }, [packageType, updateField]);

  useEffect(() => {
    const resetSubmittingState = () => {
      setSubmitting(false);
    };

    const handlePageShow = () => {
      resetSubmittingState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetSubmittingState();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const minUkDate = useMemo(() => getTodayUK(), []);

  const ensureCoverageText = (value) => {
    if (!value) return "";

    const original = String(value).trim();
    const lower = original.toLowerCase();

    if (lower.includes("coverage")) return original;

    return `${original} coverage`;
  };

  const loadPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      setPackagesError("");

      const endpoint = packageType === "general" ? "get_event_packages" : "get_wedding_packages";
      const res = await apiFetch(endpoint);

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load packages.");
      }

      const mappedPackages = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name,
          priceFrom: Number(item.price),
          duration: ensureCoverageText(item.coverage),
          highlights: Array.isArray(item.features) ? item.features : [],
        }),
      ).sort((a, b) => a.priceFrom - b.priceFrom || a.id - b.id);

      setPackages(mappedPackages);
      setSelectedPackageId((prev) =>
        prev && mappedPackages.some((pkg) => pkg.id === Number(prev))
          ? Number(prev)
          : mappedPackages.length
            ? mappedPackages[0].id
            : null,
      );
    } catch (err) {
      console.error("Failed to load packages:", err);
      setPackagesError(
        "We couldn't load our packages right now. Please refresh the page or contact us for pricing.",
      );
      setPackages([]);
      setSelectedPackageId(null);
    } finally {
      setLoadingPackages(false);
    }
  }, [apiFetch, packageType]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const selectedPackage = useMemo(() => {
    return (
      packages.find((p) => Number(p.id) === Number(selectedPackageId)) ||
      packages[0] ||
      null
    );
  }, [packages, selectedPackageId]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const onSelectPackage = (id) => {
    setSelectedPackageId(id);

    if (isErrorOpen) {
      closeError();
    }

  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPackage) {
      showError("Please select a package.");
      return;
    }

    if (!form.fullName.trim()) {
      showError("Please enter your full name.");
      return;
    }

    if (!form.email.trim()) {
      showError("Please enter your email address.");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      showError("Please enter a valid email address.");
      return;
    }

    if (!form.phone.trim()) {
      showError("Please enter your phone number.");
      return;
    }

    if (!form.eventDate) {
      showError("Please choose an event date.");
      return;
    }

    if (!form.location.trim()) {
      showError("Please enter the event location.");
      return;
    }

    if (!form.startTime.trim()) {
      showError("Please enter the event start time.");
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        packageId: selectedPackage.id,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        eventType: form.eventType.trim(),
        eventDate: form.eventDate,
        location: form.location.trim(),
        startTime: form.startTime.trim(),
        coverageNotes: form.coverageNotes.trim(),
        packageType: packageType,
      };

      const endpoint = "create_event_checkout_session";
      const res = await apiFetch(endpoint, payload);

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkoutUrl) {
        throw new Error("Stripe checkout link was not returned.");
      }

      window.location.href = res.checkoutUrl;
    } catch (err) {
      showError(err.message || "Unable to start payment.");
      setSubmitting(false);
    }
  };

  const stepBadge = (n) => <StepNumber n={n} />;

  const cardClass =
    "relative isolate overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] md:p-8";

  const inputClass =
    "w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-black outline-none transition focus:border-pink-500";

  return (
    <>
      <SEO
        title={PAGES["/packages"].title}
        description={PAGES["/packages"].description}
      />
      <main className="event-packages-page min-h-screen bg-white text-black">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator,
          input[type="time"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"],
          input[type="time"] {
            color-scheme: light;
          }
        `}</style>

        <PageHero
          title="Professional Event Photography Packages"
          image={heroPackages}
          imagePosition="center 30%"
        />

        <PageIntro>
          Photo and video coverage for weddings, birthdays, corporate events and special celebrations. Choose the package that fits your day, add your event details and make payment.
        </PageIntro>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 pb-6 pt-10 sm:px-10 lg:px-16 xl:px-6">
            <div className="flex w-fit rounded-2xl border border-black/10 bg-white p-1.5 shadow-sm">
              <button
                type="button"
                onClick={() => {
                  setPackageType("wedding");
                  updateField("eventType", "Wedding");
                }}
                className={`rounded-xl px-8 py-3 text-base font-medium transition-all duration-300 ${
                  packageType === "wedding"
                    ? "bg-pink-500 text-white shadow-md"
                    : "text-black/60 hover:bg-black/5 hover:text-black"
                }`}
              >
                Weddings
              </button>
              <button
                type="button"
                onClick={() => {
                  setPackageType("general");
                  updateField("eventType", "");
                }}
                className={`rounded-xl px-8 py-3 text-base font-medium transition-all duration-300 ${
                  packageType === "general"
                    ? "bg-pink-500 text-white shadow-md"
                    : "text-black/60 hover:bg-black/5 hover:text-black"
                }`}
              >
                General Events
              </button>
            </div>
          </div>

          {!loadingPackages && !packagesError && packages.length === 0 ? (
            <div className="mx-auto max-w-7xl px-6 pb-16 sm:px-10 lg:px-16 xl:px-6">
              <div className={`${cardClass} mx-auto max-w-3xl text-center`}>
                <h2 className="text-2xl font-semibold md:text-3xl">
                  {packageType === "wedding"
                    ? "Wedding packages are being updated"
                    : "General event packages are tailored to you"}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-pretty text-base leading-relaxed text-black/65">
                  {packageType === "wedding"
                    ? "We don't have wedding packages available to book online right now. Tell us your date and we will put together coverage that suits your day."
                    : "Birthdays, corporate events, christenings and other celebrations are quoted individually. Tell us about your event and we will send you a price and what is included."}
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                  <a
                    href={whatsappLink(WHATSAPP_MESSAGES.events)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 text-[15px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-pink-600 active:scale-95"
                  >
                    Get a Quote on WhatsApp
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-black/80 px-7 py-3 text-[15px] font-semibold text-black transition hover:-translate-y-0.5 hover:bg-black hover:text-white active:scale-95"
                  >
                    Send an Enquiry
                  </Link>
                </div>
                <p className="mt-6 text-sm text-black/55">
                  Prefer to talk? Call{" "}
                  <a
                    href="tel:+447486160965"
                    className="font-medium text-pink-600 hover:text-pink-700"
                  >
                    +44 7486 160965
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const next = packageType === "wedding" ? "general" : "wedding";
                    setPackageType(next);
                    updateField("eventType", next === "wedding" ? "Wedding" : "");
                  }}
                  className="mt-4 text-sm font-medium text-black/60 underline underline-offset-4 hover:text-black"
                >
                  {packageType === "wedding"
                    ? "See general event packages"
                    : "See wedding packages"}
                </button>
              </div>
            </div>
          ) : (
          <div className="mx-auto grid max-w-7xl gap-8 px-6 pb-16 sm:px-10 lg:grid-cols-12 lg:px-16 xl:px-6">
            <div className="space-y-8 lg:col-span-7">
              {/* Step 1 */}
              <div className={cardClass}>
                <div className="mb-6 flex items-center gap-4">
                  {stepBadge(1)}
                  <div>
                    <h2 className="text-2xl font-semibold">
                      Choose a package
                    </h2>
                    <p className="text-sm text-black/50">
                      {packageType === "wedding"
                        ? "Wedding packages"
                        : "General event packages"}
                    </p>
                  </div>
                </div>

                {loadingPackages ? (
                  <div className="rounded-2xl border border-black/10 bg-white p-6 text-black/60">
                    Loading packages...
                  </div>
                ) : packagesError ? (
                  <div className="rounded-2xl border border-red-500/20 bg-red-50 p-6 text-red-600">
                    {packagesError}
                  </div>
                ) : packages.length === 0 ? (
                  <div className="rounded-2xl border border-black/10 bg-white p-6 text-black/60">
                    No packages are available right now.
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    {packages.map((p) => {
                      const active = Number(p.id) === Number(selectedPackageId);

                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => onSelectPackage(p.id)}
                          className={[
                            "flex h-full flex-col rounded-2xl border p-5 text-left transition-all duration-300",
                            active
                              ? "border-pink-500 bg-gradient-to-b from-pink-50 to-white shadow-[0_0_0_1px_rgba(236,72,153,0.25),0_20px_50px_rgba(236,72,153,0.15)]"
                              : "border-black/10 bg-white hover:-translate-y-1 hover:border-pink-300 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]",
                          ].join(" ")}
                        >
                          <h3 className="text-lg font-semibold text-black/80">
                            {p.name}
                          </h3>
                          <p
                            className={[
                              "mt-1 text-4xl font-bold tracking-tight",
                              active ? "text-pink-600" : "text-black",
                            ].join(" ")}
                          >
                            {displayPrice(p.priceFrom)}
                          </p>
                          <p className="mt-2 text-sm text-black/60">
                            {p.duration}
                          </p>

                          <ul className="mt-5 space-y-2.5 text-sm text-black/75">
                            {p.highlights.map((x, index) => (
                              <li
                                key={`${p.id}-${index}-${x}`}
                                className="flex items-start gap-2"
                              >
                                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-pink-400" />
                                <span>{x}</span>
                              </li>
                            ))}
                          </ul>
                        </button>
                      );
                    })}
                  </div>
                )}

                <p className="mt-6 text-sm text-black/55">
                  All digital images are high resolution with print release
                  licence.
                </p>
              </div>

              {/* Step 2 */}
              <form
                id="wedding-package-form"
                noValidate
                onSubmit={onSubmit}
                className={cardClass}
              >
                <div className="mb-6 flex items-center gap-4">
                  {stepBadge(2)}
                  <h2 className="text-2xl font-semibold">
                    Your event details
                  </h2>
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label className="text-sm font-medium text-black/70">
                      Full name
                    </label>
                    <input
                      className={inputClass}
                      value={form.fullName}
                      onChange={onChange("fullName")}
                      required
                      autoComplete="name"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Email address
                    </label>
                    <input
                      type="email"
                      className={inputClass}
                      value={form.email}
                      onChange={onChange("email")}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Phone number
                    </label>
                    <input
                      className={inputClass}
                      value={form.phone}
                      onChange={onChange("phone")}
                      required
                      autoComplete="tel"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Event date
                    </label>
                    <div className="relative min-w-0">
                      <input
                        type="date"
                        min={minUkDate}
                        className={`${inputClass} min-w-0 appearance-none`}
                        value={form.eventDate}
                        onChange={onChange("eventDate")}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Event time
                    </label>
                    <div className="relative min-w-0">
                      <input
                        type="time"
                        className={`${inputClass} min-w-0 appearance-none`}
                        value={form.startTime}
                        onChange={onChange("startTime")}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Location
                    </label>
                    <input
                      className={inputClass}
                      value={form.location}
                      onChange={onChange("location")}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-black/70">
                      Event type (occasion)
                    </label>
                    <input
                      className={inputClass}
                      value={form.eventType}
                      onChange={onChange("eventType")}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 sm:col-span-2">
                    <label className="text-sm font-medium text-black/70">
                      Additional notes
                    </label>
                    <textarea
                      className={inputClass}
                      rows={4}
                      value={form.coverageNotes}
                      onChange={onChange("coverageNotes")}
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Summary */}
            <aside className="lg:col-span-5">
              <div className="rounded-3xl border border-pink-500/20 bg-[#1c1c1c] p-7 text-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] lg:sticky lg:top-28">
                <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                  Checkout
                </p>
                <h3 className="mt-2 text-2xl font-semibold">Order Summary</h3>

                {!selectedPackage ? (
                  <div className="mt-8 text-sm text-white/70">
                    No package selected.
                  </div>
                ) : (
                  <div className="mt-8 space-y-4 text-sm text-white/75">
                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Package</span>
                      <span className="text-right font-medium text-white">
                        {selectedPackage.name}
                      </span>
                    </div>

                    <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                      <span>Coverage</span>
                      <span className="text-right font-medium text-white">
                        {selectedPackage.duration}
                      </span>
                    </div>

                    <div className="border-b border-white/10 pb-3">
                      <span className="mb-3 block">Package Features</span>

                      {selectedPackage.highlights?.length ? (
                        <ul className="space-y-2">
                          {selectedPackage.highlights.map((feature, index) => (
                            <li
                              key={`summary-feature-${selectedPackage.id}-${index}`}
                              className="flex items-start gap-2 text-white/85"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-400" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-white/60">
                          No package features listed.
                        </span>
                      )}
                    </div>

                    <div className="mt-6 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-4">
                      <span className="text-base font-semibold text-white">
                        Total
                      </span>
                      <span className="text-2xl font-semibold text-pink-400">
                        {money(selectedPackage.priceFrom)}
                      </span>
                    </div>

                    <button
                      type="submit"
                      form="wedding-package-form"
                      disabled={submitting || loadingPackages}
                      className={`w-full rounded-2xl py-4 text-base font-semibold transition ${
                        submitting || loadingPackages
                          ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                          : "bg-pink-500 text-white hover:bg-pink-400"
                      }`}
                    >
                      {submitting ? "Redirecting..." : "Continue to Payment"}
                    </button>

                    <p className="text-center text-xs leading-relaxed text-white/50">
                      You will be taken to a secure payment page to complete
                      your booking.
                    </p>
                  </div>
                )}

                <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/70">
                  <p className="font-medium text-white">
                    Planning something bigger?
                  </p>
                  <p className="mt-2">
                    Call{" "}
                    <a
                      href="tel:+447486160965"
                      className="text-pink-400 hover:text-pink-300"
                    >
                      +44 7486 160965
                    </a>{" "}
                    or{" "}
                    <a
                      href={whatsappLink(WHATSAPP_MESSAGES.events)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-400 hover:text-pink-300"
                    >
                      message us on WhatsApp
                    </a>
                    .
                  </p>
                </div>
              </div>
            </aside>
          </div>
          )}
        </section>

        <section className="bg-[#fff7fb]">
          <div className="mx-auto max-w-7xl px-6 py-16 sm:px-10 md:py-20 lg:px-16 xl:px-6">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-balance text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
                Events we cover
              </h2>
              <p className="mt-4 text-pretty text-base leading-relaxed text-black/65 md:text-lg">
                Alongside weddings, we cover private and corporate occasions.
                Here are some of the celebrations we cover.
              </p>
            </div>

            <ul className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-3">
              {[
                "Birthday celebrations",
                "Anniversaries",
                "Engagement parties",
                "Baby showers",
                "Christenings",
                "Weddings & receptions",
                "Corporate events",
                "Award ceremonies",
                "Community events",
                "Private celebrations",
              ].map((event) => (
                <li
                  key={event}
                  className="rounded-full border border-pink-200 bg-white px-5 py-2.5 text-[15px] font-medium text-black/80 shadow-[0_8px_20px_rgba(0,0,0,0.04)]"
                >
                  {event}
                </li>
              ))}
              <li className="rounded-full border border-dashed border-pink-400 px-5 py-2.5 text-[15px] font-semibold text-pink-600">
                &amp; more
              </li>
            </ul>

            <p className="mx-auto mt-8 max-w-2xl text-center text-pretty text-[15px] leading-relaxed text-black/60">
              Have something else in mind?{" "}
              <Link
                to="/contact"
                className="font-semibold text-pink-600 underline underline-offset-4 transition hover:text-pink-500"
              >
                Tell us about your event
              </Link>{" "}
              and we will help. We cover events across the UK.
            </p>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 py-14 sm:px-10 lg:px-16 xl:px-6">
            <CtaBand
              heading="Hire a Photography Studio in Rochdale"
              text="Rent our fully equipped studio by the hour for photoshoots, video shoots and content creation."
              to="/rentals"
              label="Rent Our Studio"
              image={ctaRentals}
            />
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}
